// src/fleet-stats/components/InternalOverview.jsx
//
// Replaces the headline + fleet board + attention stack when the Internal lens
// is selected. Those three all frame a robot as a fleet asset that should be
// working; here a robot is a unit passing through the build floor, so the
// summary is throughput and lifecycle instead of utilisation and exceptions.

import React, { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { C, CARD_BG, CARD_BORDER } from "../constants.js";
import { fmtInt, fmtNum } from "../utils/format.js";
import { DataTable, brandButton, hoverNeon, unhoverNeon } from "./SharedUI.jsx";

const BAR_BLUE = "#4a9ebb";   // same accent the overview charts use

const MONTH_LABEL = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function fmtMonth(key) {
    const [y, m] = (key || "").split("-");
    const i = Number(m) - 1;
    return MONTH_LABEL[i] ? `${MONTH_LABEL[i]} ${y.slice(2)}` : key;
}

/* -------- commissioning volume, most recent 12 months -------- */
function CommissioningBars({ commissioned }) {
    const recent = commissioned.slice(-12);
    const max = recent.reduce((m, e) => Math.max(m, e.count), 0) || 1;

    if (recent.length === 0) return null;

    return (
        <div className="rounded-xl p-4" style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: C.grey2 }}>
                Robots first seen, per month
            </div>
            <div className="flex flex-col gap-1.5">
                {recent.map((e) => (
                    <div key={e.month} className="flex items-center gap-3">
                        <span className="text-xs tabular-nums w-14 shrink-0" style={{ color: C.grey2 }}>
                            {fmtMonth(e.month)}
                        </span>
                        <div className="flex-1 h-4 rounded" style={{ backgroundColor: "rgba(255,255,255,0.04)" }}>
                            <div
                                className="h-4 rounded"
                                style={{ width: `${(e.count / max) * 100}%`, backgroundColor: BAR_BLUE, minWidth: 2 }}
                            />
                        </div>
                        <span className="text-xs tabular-nums w-8 text-right" style={{ color: C.offWhite }}>
                            {e.count}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Stat({ label, value, hint }) {
    return (
        <div className="rounded-xl p-4" style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}>
            <div className="text-2xl font-bold" style={{ color: C.offWhite }}>{value}</div>
            <div className="text-sm mt-0.5" style={{ color: C.grey1 }}>{label}</div>
            {hint && <div className="text-xs mt-1" style={{ color: C.grey2 }}>{hint}</div>}
        </div>
    );
}

export function InternalOverview({ view, onSelectRobot }) {
    const [sortKey, setSortKey] = useState("lastSeen");
    const [sortDir, setSortDir] = useState("desc");

    const sorted = useMemo(() => {
        if (!view) return [];
        const dir = sortDir === "asc" ? 1 : -1;
        return [...view.lifecycle].sort((a, b) => {
            const x = a[sortKey], y = b[sortKey];
            // Nulls last regardless of direction — "still here" is not a value
            // to be ordered against a site name, it is the absence of one.
            if (x === null && y === null) return 0;
            if (x === null) return 1;
            if (y === null) return -1;
            if (x === y) return 0;
            return x < y ? -dir : dir;
        });
    }, [view, sortKey, sortDir]);

    if (!view) return null;

    const toggleSort = (key) => {
        if (key === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
        else { setSortKey(key); setSortDir(key === "robotName" ? "asc" : "desc"); }
    };

    const header = (label, key, align) => (
        <button
            onClick={() => toggleSort(key)}
            className="font-medium transition-colors"
            style={{ color: sortKey === key ? C.neonGreen : C.grey2, textAlign: align || "left" }}
            title={`Sort by ${label.toLowerCase()}`}
        >
            {label}{sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
        </button>
    );

    const columns = [
        { key: "robotName", label: header("Robot", "robotName"),
          render: (r) => (
              <button
                  onClick={() => onSelectRobot && onSelectRobot(r.robotName)}
                  className="transition-colors text-left"
                  style={{ color: C.offWhite }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = C.offWhite)}
              >
                  {r.robotName}
              </button>
          ) },
        { key: "siteLabel", label: header("Site", "siteLabel") },
        { key: "firstSeen", label: header("First seen", "firstSeen") },
        { key: "lastSeen", label: header("Last seen", "lastSeen") },
        { key: "days", label: header("Days", "days", "right"), align: "right",
          render: (r) => fmtInt(r.days) },
        { key: "testHours", label: header("Test h", "testHours", "right"), align: "right",
          render: (r) => fmtNum(r.testHours, 1) },
        { key: "destination", label: header("Now at", "destination"),
          render: (r) => r.destination
              ? <span style={{ color: C.offWhite }}>{r.destination}<span style={{ color: C.grey2 }}> · {r.destinationDate}</span></span>
              : <span style={{ color: C.grey2 }}>still here</span> },
    ];

    const downloadCsv = () => {
        const head = ["robot_id", "robot_name", "site", "first_seen", "last_seen",
                      "days_present", "test_hours", "km", "now_at", "now_at_date"];
        const lines = [head.join(",")];
        for (const r of sorted) {
            lines.push([
                r.robotId, JSON.stringify(r.robotName), JSON.stringify(r.siteLabel),
                r.firstSeen, r.lastSeen, r.days, r.testHours.toFixed(2), r.km.toFixed(3),
                JSON.stringify(r.destination || ""), r.destinationDate || "",
            ].join(","));
        }
        const blob = new Blob([lines.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `internal_robot_lifecycle_${view.maxDate}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="flex flex-col gap-4">
            {/* ── Summary sentence ── */}
            <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: C.grey2 }}>
                    Internal — build floor
                </div>
                <h2 className="text-3xl leading-snug tracking-tight" style={{ color: C.grey1 }}>
                    <span style={{ color: C.offWhite, fontWeight: 600 }}>{fmtInt(view.thisMonth)}</span>
                    {" robot"}{view.thisMonth === 1 ? "" : "s"}{" first seen this month · "}
                    <span style={{ color: C.offWhite, fontWeight: 600 }}>{fmtInt(view.onFloor.length)}</span>
                    {" on the floor now"}
                </h2>
                <div className="text-sm mt-2" style={{ color: C.grey2 }}>
                    {fmtInt(view.totalRobots)} robots have passed through ·{" "}
                    {fmtNum(view.testHours, 0)} h test time ·{" "}
                    {fmtNum(view.km, 1)} km driven · data through {view.maxDate}
                </div>
                <div className="text-xs mt-2" style={{ color: C.grey2 }}>
                    Utilisation, readiness and offline flags are not shown here — a robot
                    sitting idle on the build floor is the normal end state, not an exception.
                </div>
            </div>

            {/* ── Stats + commissioning ── */}
            <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                <Stat label="On the floor" value={fmtInt(view.onFloor.length)} hint="seen in the last 7 days" />
                <Stat label="Shipped to customers" value={fmtInt(view.shipped.length)} hint="traced to a customer or partner site" />
                <Stat label="Never left" value={fmtInt(view.totalRobots - view.shipped.length)} hint="no record at any other site" />
            </div>

            <CommissioningBars commissioned={view.commissioned} />

            {/* ── Lifecycle table ── */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: C.grey2 }}>
                        Robot lifecycle
                    </div>
                    <button
                        onClick={downloadCsv}
                        className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                        style={brandButton()}
                        onMouseEnter={hoverNeon}
                        onMouseLeave={unhoverNeon}
                    >
                        <Download size={14} /> CSV
                    </button>
                </div>
                <DataTable
                    columns={columns}
                    rows={sorted.map((r) => ({ ...r, _key: `${r.siteLabel}:${r.robotId}` }))}
                    maxVisibleRows={12}
                    emptyMessage="No internal robots in range."
                />
            </div>
        </div>
    );
}
